"use client";

import { useQuery } from "@apollo/client";
import {
  Card,
  LiquidPill,
  PageEmptyStateV2,
  PageLoadingStateV2,
} from "components/ui/v2";
import {
  Award,
  CalendarCheck2,
  CalendarX2,
  CheckCircle2,
  ClockAlert,
  Download,
  ExternalLink,
  XCircle,
} from "lucide-react";
import { gqlUser } from "gql";
import moment from "moment";
import useTranslation from "next-translate/useTranslation";
import type { IUser, IUserCertification } from "types/User.types";
import { ETypeCertification } from "types/User.types";

export interface UserCertificationsTabV2Props {
  userCertificated?: IUser;
  hideHeader?: boolean;
}

function getCertificationStatus(expiracyDate?: Date) {
  if (!expiracyDate) return "active";
  const daysUntilExpiry = moment(expiracyDate).diff(moment(), "days");
  if (daysUntilExpiry < 0) return "expired";
  if (daysUntilExpiry <= 30) return "expiring-soon";
  return "active";
}

function statusIcon(status: string) {
  if (status === "expired") return <XCircle className="h-3 w-3" />;
  if (status === "expiring-soon") return <ClockAlert className="h-3 w-3" />;
  return <CheckCircle2 className="h-3 w-3" />;
}

function statusVariant(status: string) {
  if (status === "expired") return "rose" as const;
  if (status === "expiring-soon") return "gold" as const;
  return "emerald" as const;
}

function CertificationReadOnlyCardV2({
  certification,
}: {
  certification: IUserCertification;
}) {
  const { t } = useTranslation("common");
  const { name, type, expiracyDate, certificationUrl, url, emitDate, certificationType } =
    certification;
  const status = getCertificationStatus(expiracyDate);

  const handleDownload = (downloadUrl: string) => window.open(downloadUrl, "_blank");

  return (
    <Card size="full" variant="default" padding="none" shadow="subtle" fullHeight entranceAnimation>
      <article className="flex h-full min-h-[150px] flex-col overflow-hidden">
        <div className="flex flex-1 flex-col p-3 text-left">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <Award className="mt-1 h-5 w-5 shrink-0 text-indigo-500 dark:text-indigo-300" />
            <div className="min-w-0 flex-1">
              <h3 className="line-clamp-2 text-base font-semibold leading-tight text-slate-950 dark:text-white">
                {name}
              </h3>
              <p className="mt-1 text-xs font-medium text-slate-500 dark:text-zinc-400">
                {type}
              </p>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <LiquidPill
              as="span"
              variant={statusVariant(status)}
              appearance="tinted"
              shadow="neutral"
              size="xs"
              icon={statusIcon(status)}
              text={
                status === "expired"
                  ? t("Expirada")
                  : status === "expiring-soon"
                    ? t("Expira pronto")
                    : t("Activa")
              }
            />
            <LiquidPill
              as="span"
              variant="indigo"
              appearance="tinted"
              shadow="neutral"
              size="xs"
              text={certificationType || ETypeCertification.CERTIFICATION}
            />
          </div>

          <div className="mb-2.5 mt-3 flex flex-wrap items-center justify-center gap-1.5 text-[11px] text-slate-500 dark:text-zinc-400">
            {emitDate ? (
              <>
                <CalendarCheck2 className="h-3 w-3 text-slate-400" />
                <span>
                  {t("Emitida el")}{" "}
                  <span className="font-medium text-slate-600 dark:text-zinc-300">
                    {moment(emitDate).format("DD/MM/YYYY")}
                  </span>
                </span>
              </>
            ) : null}
            {emitDate && expiracyDate ? (
              <span className="text-slate-300 dark:text-zinc-700">·</span>
            ) : null}
            {expiracyDate ? (
              <>
                <CalendarX2 className="h-3 w-3 text-slate-400" />
                <span>
                  {t("Válida hasta")}{" "}
                  <span className="font-medium text-slate-600 dark:text-zinc-300">
                    {moment(expiracyDate).format("DD/MM/YYYY")}
                  </span>
                </span>
              </>
            ) : null}
          </div>

          {url || certificationUrl ? (
            <div className="mt-auto border-t border-slate-200/70 pt-2.5 dark:border-zinc-800/80">
              <div className={`grid gap-2 ${url && certificationUrl ? "grid-cols-2" : "grid-cols-1"}`}>
                {url ? (
                  <LiquidPill
                    as="button"
                    type="button"
                    variant="sky"
                    appearance="tinted"
                    shadow="neutral"
                    size="card-cta"
                    icon={<Download className="h-3.5 w-3.5" />}
                    text={t("Descargar")}
                    onClick={() => handleDownload(url)}
                  />
                ) : null}
                {certificationUrl ? (
                  <LiquidPill
                    as="button"
                    type="button"
                    variant="indigo"
                    appearance="tinted"
                    shadow="neutral"
                    size="card-cta"
                    icon={<ExternalLink className="h-3.5 w-3.5" />}
                    text={t("Ver certificación")}
                    onClick={() => handleDownload(certificationUrl)}
                  />
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </article>
    </Card>
  );
}

export function UserCertificationsTabV2({
  userCertificated,
  hideHeader = false,
}: UserCertificationsTabV2Props) {
  const { t } = useTranslation("common");

  const { data, loading } = useQuery<
    { userCertifications: IUserCertification[] },
    { userId: string }
  >(gqlUser.queries.GET_USER_CERTIFICATIONS, {
    variables: {
      userId: userCertificated?.id || "",
    },
    skip: !userCertificated?.id,
  });

  if (loading) {
    return (
      <PageLoadingStateV2
        title={t("Cargando certificaciones")}
        description={t("Espere por favor")}
      />
    );
  }

  return (
    <div className="space-y-4">
      {!hideHeader ? (
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            {t("Certificaciones")}
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-zinc-400">
            {t("Estas son las certificaciones que ha obtenido este Colaborador")}
          </p>
        </div>
      ) : null}

      {data?.userCertifications?.length ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {data.userCertifications.map((certification) => (
            <CertificationReadOnlyCardV2
              key={certification.id}
              certification={certification}
            />
          ))}
        </div>
      ) : (
        <PageEmptyStateV2
          variant="empty"
          icon={<Award className="h-8 w-8" />}
          title={t("Este colaborador aún no tiene certificaciones")}
          description={t(
            "Las certificaciones aparecerán aquí cuando sean agregadas al perfil"
          )}
        />
      )}
    </div>
  );
}
